import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";
import { useTheme } from "@/hooks/useTheme";
import { HeaderIcon } from "@/components/Layout/header/HeaderIcon";
import type { AppLanguage } from "@/constants/i18n";
import type { AppTheme } from "@/constants/theme";
import { getNextTheme } from "@/constants/theme";
import "./HeaderPreferenceControls.css";

interface HeaderPreferenceControlsProps {
  variant: "desktop" | "mobile";
}

export function HeaderPreferenceControls({
  variant,
}: HeaderPreferenceControlsProps) {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { lang, toggleLanguage } = useLanguage();

  return (
    <div className={`site-header__preferences site-header__preferences--${variant}`}>
      <LanguageToggleButton
        lang={lang}
        label={t("a11y.language")}
        onClick={toggleLanguage}
        withChevron={false}
      />
      <ThemeToggleButton
        label={t("a11y.toggleTheme")}
        onClick={toggleTheme}
        theme={theme}
      />
    </div>
  );
}

function LanguageToggleButton({
  lang,
  label,
  onClick,
  withChevron = false,
}: {
  lang: AppLanguage;
  label: string;
  onClick: () => void;
  withChevron?: boolean;
}) {
  return (
    <button
      type="button"
      className="site-header__lang"
      onClick={onClick}
      aria-label={`${label}: ${lang.toUpperCase()}`}
    >
      <span>{lang.toUpperCase()}</span>
      {withChevron ? <HeaderIcon name="chevron" /> : null}
    </button>
  );
}

function ThemeToggleButton({
  label,
  onClick,
  theme,
}: {
  label: string;
  onClick: () => void;
  theme: AppTheme;
}) {
  return (
    <button
      type="button"
      className="site-header__theme-toggle"
      onClick={onClick}
      aria-label={label}
      aria-pressed={theme === "dark"}
    >
      <HeaderIcon name={getNextTheme(theme)} />
    </button>
  );
}
